import { Injectable, Injector } from '@angular/core';
import { User, UserInfo } from '../../entities/User';
import { Credentials } from '../../entities/Credentials';
import { environment } from '../../../environments/environment';
import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { BehaviorSubject, catchError, finalize, map, Observable, of, retry, shareReplay, Subject, switchMap, tap, throwError } from 'rxjs';
import { BaseServicesService } from './base.service';
import { Router } from '@angular/router';
import { CambiarPassword } from 'src/app/entities/cambiarPassword';
import { ResetPassword } from './ResetPassword';

/** Respuesta de POST /auth */
export interface AuthTokenResponse {
  token: string;
  refreshToken: string;
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService extends BaseServicesService {
  private authenticationChanged = new Subject<boolean>();
  private user = new User();

   private imagenPerfilSubject = new BehaviorSubject<string>(
    this.getUser() && this.getUser().user ? this.getUser().user.imagenPerfil : ''
  );
  
   public imagenPerfil$ = this.imagenPerfilSubject.asObservable();
 
   updateImagenPerfil(newImageUrl: string) {
     const currentUser = this.getUser();
     currentUser.user.imagenPerfil = newImageUrl;
     sessionStorage.setItem('user', JSON.stringify(currentUser));
 
     this.imagenPerfilSubject.next(newImageUrl);
   }

   canActivate(): boolean {
    const authService = this.injector.get(AuthenticationService);
    if (authService.isAuthenticated()) {
      return true;
    } else {
      this.router.navigate(['/login']);
      return false;
    }
  }

  /** Evita recursión de interceptores al llamar POST /auth/refresh */
  private refreshAuthInFlight$: Observable<AuthTokenResponse> | null = null;

  constructor(
    private injector: Injector,
    private http: HttpClient,
    private httpBackend: HttpBackend,
    private router: Router,
  ) {
    super();
  }

  public isAuthenticated(): boolean {
    return this.getToken().length > 0;
  }

  public isAuthenticationChanged(): any {
    return this.authenticationChanged.asObservable();
  }

  clearUserData() {
    this.user = null;
    sessionStorage.clear();
    this.authenticationChanged.next(false);
  }

  public getToken(): string {
    const raw = sessionStorage.getItem("token");
    if (
      raw === undefined ||
      raw === null ||
      raw === "null" ||
      raw === "undefined" ||
      raw === ""
    ) {
      return "";
    }
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === "string") {
        return parsed;
      }
      if (parsed && typeof parsed === "object" && typeof (parsed as { token?: string }).token === "string") {
        return (parsed as { token: string }).token;
      }
      return "";
    } catch {
      return "";
    }
  }

  public getRefreshToken(): string {
    const raw = sessionStorage.getItem("refreshToken");
    if (!raw || raw === "null" || raw === "undefined") {
      return "";
    }
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === "string" ? parsed : "";
    } catch {
      return "";
    }
  }

  /**
   * Permisos a persistir: el API a veces solo envía `user.permisos` (p. ej. autologin sin GET /auth/me).
   */
  private permissionsToPersistFromSessionPayload(data: User | null | undefined): any[] {
    if (!data) {
      return [];
    }
    const top = data.permisos;
    const nested = data.user?.permisos;
    if (Array.isArray(top) && top.length > 0) {
      return top;
    }
    return Array.isArray(nested) ? nested : [];
  }

  public setData(data: User, refreshToken?: string): void {
    this.setStorageToken(data.token);
    if (refreshToken) {
      sessionStorage.setItem("refreshToken", JSON.stringify(refreshToken));
    }
    this.setStoragePermissions(this.permissionsToPersistFromSessionPayload(data));
    this.setStorageUser(data);
  }

  public failToken(): void {
    this.cleanSession();
  }

  /**
   * Invalida el refresh en servidor (POST /auth/logout) y luego limpia sesión local.
   */
  public logout(): void {
    const rt = this.getRefreshToken();
    const finish = () => {
      this.cleanSession();
      this.router.navigate(["/login"]);
    };
    if (!rt) {
      finish();
      return;
    }
    const plain = new HttpClient(this.httpBackend);
    plain
      .post(
        `${environment.API_SECURITY}/auth/logout`,
        { refreshToken: rt },
        { headers: new HttpHeaders({ "Content-Type": "application/json" }) }
      )
      .pipe(
        catchError(() => of(null)),
        finalize(() => finish())
      )
      .subscribe();
  }

  /** Persiste par token + refreshToken y alinea `user.token` en sessionStorage */
  private persistAuthTokens(res: AuthTokenResponse): void {
    this.setStorageToken(res.token);
    if (res.refreshToken) {
      sessionStorage.setItem("refreshToken", JSON.stringify(res.refreshToken));
    }
    try {
      const raw = sessionStorage.getItem("user");
      if (raw) {
        const u = JSON.parse(raw) as User;
        if (u && typeof u === "object") {
          u.token = res.token;
          sessionStorage.setItem("user", JSON.stringify(u));
        }
      }
    } catch {
      /* ignore */
    }
    this.authenticationChanged.next(this.isAuthenticated());
  }

  /**
   * POST /auth/refresh (sin pasar por interceptores HTTP).
   * Varias peticiones 401 comparten la misma renovación en curso.
   */
  refreshAuthTokens(): Observable<AuthTokenResponse> {
    if (this.refreshAuthInFlight$) {
      return this.refreshAuthInFlight$;
    }
    const rt = this.getRefreshToken();
    if (!rt) {
      return throwError(() => new Error("No refresh token"));
    }
    const plain = new HttpClient(this.httpBackend);
    this.refreshAuthInFlight$ = plain
      .post<AuthTokenResponse>(
        `${environment.API_SECURITY}/auth/refresh`,
        { refreshToken: rt },
        {
          headers: new HttpHeaders({ "Content-Type": "application/json" }),
        }
      )
      .pipe(
        tap((tokens) => this.persistAuthTokens(tokens)),
        catchError((err) => {
          this.failToken();
          return throwError(() => err);
        }),
        finalize(() => {
          this.refreshAuthInFlight$ = null;
        }),
        shareReplay(1)
      );
    return this.refreshAuthInFlight$;
  }

  private setStorageToken(value: any): void {
    let _value = JSON.stringify(value);
    sessionStorage.setItem("token", _value);
    this.authenticationChanged.next(this.isAuthenticated());
  }

  public changePassword(passwords: CambiarPassword): Observable<boolean> {
		return this.http.put<boolean>(environment.API_SECURITY + '/api/Authentication/CambiarPassword', passwords);
	}

  changePasswordPerfil(passwords: any): Observable<HttpResponse<string>> {
    return this.http.put(`${environment.API_SECURITY}/usuarios/update/password`, passwords, {
      observe: 'response',
      responseType: 'text'
    }).pipe(
      catchError((err: HttpErrorResponse) => {
        let errorMessage = 'Ocurrió un error al actualizar la contraseña.';
        if (err.status === HttpStatusCode.BadRequest) {
          errorMessage = 'Contraseña actual incorrecta.';
        } else if (err.status === HttpStatusCode.NotFound) {
          errorMessage = 'El recurso solicitado no fue encontrado.';
        } else if (err.status === HttpStatusCode.InternalServerError) {
          errorMessage = 'Ocurrió un error en el servidor.';
        }
        return throwError(() => errorMessage);
      })
    );
  }

  public solicitarCambioPasswordByEmail(email: string): Observable<any> {
    return this.http.post<any>(`${environment.API_SECURITY}/usuarios/forgot-password`, { email });
  }
  
  public resetPassword(body: any): Observable<string> {
    return this.http.put(`${environment.API_SECURITY}/usuarios/updateForgotPassword`, body, {
        responseType: 'text'
    }).pipe(
        catchError((err: HttpErrorResponse) => {
            let errorMessage = 'Ocurrió un error al actualizar la contraseña.';
            if (err.status === HttpStatusCode.Forbidden) {
                errorMessage = 'No tiene permisos para realizar esta acción.';
            } else if (err.status === HttpStatusCode.NotFound) {
                errorMessage = 'El recurso solicitado no fue encontrado.';
            } else if (err.status === HttpStatusCode.InternalServerError) {
                errorMessage = 'Ocurrió un error en el servidor.';
            }
            return throwError(errorMessage);
        })
    );
  }

  image(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.put(`${environment.API_SECURITY}/usuarios/update/imageProfile`, formData, {
      responseType: 'text'
    });
  }

  actualizarImagenPerfil(data: { imagenPerfil: string }): Observable<any> {
    return this.http.put(`${environment.API_SECURITY}/usuarios/actualizar-imagen-perfil`, data);
  }

  private setStorageUser(value: any): void {
    let _value = JSON.stringify(value);
    sessionStorage.setItem("user", _value);
    this.authenticationChanged.next(this.isAuthenticated());
  }

  public setStorageCoordinate(coordinates): void {
    let coords = JSON.stringify(coordinates);
    sessionStorage.setItem("coordinates", coords);
  }

  updateUsuario(id: string, form: any): Observable<any> {
    return this.http.put<any>(
      `${environment.API_SECURITY}/controlusuarios/${id}`,
      form
    ).pipe(catchError(this.handleError));
  }

  getUsuarioControl(id: string): Observable<any> {
    return this.http.get<any>(
      `${environment.API_SECURITY}/controlusuarios/${id}`
    ).pipe(catchError(this.handleError));
  }

  private setStoragePermissions(permissions: Array<any>): void {
    if (!permissions?.length) {
      sessionStorage.setItem("permissions", "[]");
      this.authenticationChanged.next(this.isAuthenticated());
      return;
    }
    const transformedPermissions = permissions
      .map((permission) => {
        if (permission == null) return null;
        if (typeof permission === "string" || typeof permission === "number") {
          return String(permission);
        }
        const id = permission.idPermiso ?? permission.IdPermiso;
        return id != null && id !== "" ? String(id) : null;
      })
      .filter((p): p is string => p != null);
    let _value = JSON.stringify(transformedPermissions);
    // console.log(_value);
    sessionStorage.setItem("permissions", _value);
    // console.log('Permisos establecidos en storage:', permissions);
    this.authenticationChanged.next(this.isAuthenticated());
  }

  public cleanSession() {
    sessionStorage.clear();
  }

  public getUser(): User {
    const user = sessionStorage.getItem("user");
    if (user) {
      return JSON.parse(user);
    } else {
      return new User();
    }
  }

  public getCoordinates() {
    return JSON.parse(sessionStorage.getItem("coordinates"));
  }

  public getPermissions(): string[] {
    const permissions = JSON.parse(sessionStorage.getItem("permissions") || "[]");
    // Convertir todos los permisos a string para que coincidan con el enum
    return permissions.map((permiso: any) => String(permiso));
  }

  /**
   * POST /auth — solo tokens. Use loginAndLoadProfile para sesión completa.
   */
  public authenticate(body: Credentials): Observable<AuthTokenResponse> {
    return this.http
      .post<AuthTokenResponse>(environment.API_SECURITY + "/auth", body)
      .pipe(catchError(this.handleError));
  }

  /** GET /auth/me — requiere Bearer ya guardado en sessionStorage.
   * Cabeceras anti-caché + parámetro `_` único: evita 304 con cuerpo vacío en XHR. */
  public getAuthMe(): Observable<any> {
    const params = new HttpParams().set("_", String(Date.now()));
    const headers = new HttpHeaders({
      "Cache-Control": "no-cache, no-store",
      Pragma: "no-cache",
      Expires: "0",
    });
    return this.http
      .get<any>(`${environment.API_SECURITY}/auth/me`, { headers, params })
      .pipe(catchError(this.handleError));
  }

  /** Login: guarda token + refreshToken, luego carga perfil con GET /auth/me */
  public loginAndLoadProfile(body: Credentials): Observable<User> {
    return this.authenticate(body).pipe(
      switchMap((authRes) => {
        this.setStorageToken(authRes.token);
        if (authRes.refreshToken) {
          sessionStorage.setItem("refreshToken", JSON.stringify(authRes.refreshToken));
        }
        return this.getAuthMe().pipe(
          map((me) => this.mapMeResponseToUser(me, authRes.token))
        );
      })
    );
  }

  /**
   * Si ya hay token (p. ej. recarga de página), sincroniza usuario y permisos con el servidor.
   */
  public refreshSessionUser(): Observable<User | null> {
    if (!this.isAuthenticated()) {
      return of(null);
    }
    const accessToken = this.getToken();
    return this.getAuthMe().pipe(
      map((me) => {
        const user = this.mapMeResponseToUser(me, accessToken);
        this.setStorageUser(user);
        this.setStoragePermissions(this.permissionsToPersistFromSessionPayload(user));
        return user;
      }),
      catchError(() => {
        this.failToken();
        return of(null);
      })
    );
  }

  /**
   * GET /auth/me: puede ser el User anidado antiguo o el payload plano (userId, userName, permisos, …).
   * El access token vigente siempre es el del login o sessionStorage.
   */
  private mapMeResponseToUser(me: any, accessToken: string): User {
    const u = new User();
    u.token = accessToken || "";

    if (!me || typeof me !== "object") {
      u.permisos = [];
      u.user = new UserInfo();
      return u;
    }

    if (me.user != null && typeof me.user === "object") {
      Object.assign(u, me);
      u.token = accessToken || u.token || "";
      u.permisos = me.permisos ?? me.user?.permisos ?? [];
      if (!u.permisos) {
        u.permisos = [];
      }
      return u;
    }

    u.permisos = me.permisos ?? [];
    u.id = me.userId;
    u.user = this.mapMeFlatToUserInfo(me);
    return u;
  }

  /** Respuesta plana actual de GET /auth/me → UserInfo que espera la UI */
  private mapMeFlatToUserInfo(me: any): UserInfo {
    const info = new UserInfo();
    info.id = me.userId;
    info.userName = me.userName ?? "";
    info.email = me.userName ?? "";
    info.idCliente = me.idCliente ?? undefined;
    info.idCadena = me.idCadena ?? undefined;
    info.idSucursal = me.idSucursal ?? 0;
    info.idCaja = me.idCaja ?? 0;
    info.idRol = me.idRol;
    info.rol = {
      id: String(me.idRol ?? ""),
      nombre: me.rol ?? "",
      estatus: 1,
    };
    info.permisos = me.permisos ?? [];
    info.validPassword = me.validPassword !== false;
    const uname = me.userName ?? "";
    info.nombre = uname.includes("@") ? uname.split("@")[0] : uname;
    info.apellidoPaterno = "";
    info.imagenPerfil = "";

    info.cliente = me.nombreCliente
      ? { nombre: me.nombreCliente, razonSocial: me.nombreCliente }
      : undefined;
    info.cadena = {
      imagenLogo: null,
      nombre: me.nombreCadena ?? "",
      razonSocial: me.nombreCadena ?? "",
    };
    info.sucursal = {
      id: me.idSucursal ?? undefined,
      nombre: me.nombreSucursal ?? "",
    };
    info.caja = me.nombreCaja ? { nombre: me.nombreCaja } : undefined;

    return info;
  }

  public autologin(token: string): Observable<User> {
    return this.http.post<User>(`${environment.API_SECURITY}/autologin/login`, { token })
      .pipe(catchError(this.handleError));
  }

  /**
   * Token de un solo uso (?token=) → JWT de sesión + GET /auth/me (mismos permisos/perfil que login con usuario/contraseña).
   * El POST /autologin/login suele no traer permisos completos; sin este paso el sidebar queda mal hasta F5.
   */
  public autologinAndLoadProfile(oneTimeToken: string): Observable<User> {
    return this.http
      .post<any>(`${environment.API_SECURITY}/autologin/login`, { token: oneTimeToken })
      .pipe(
        catchError(this.handleError),
        switchMap((res: any) => {
          const accessToken = this.pickAccessTokenFromAutologinResponse(res);
          if (!accessToken) {
            return throwError(() => ({ status: 422, message: "Autologin sin token de acceso" }));
          }
          this.setStorageToken(accessToken);
          const rt = res?.refreshToken ?? res?.refresh_token;
          if (rt) {
            sessionStorage.setItem("refreshToken", JSON.stringify(rt));
          }
          return this.getAuthMe().pipe(
            catchError(this.handleError),
            map((me) => this.mapMeResponseToUser(me, accessToken))
          );
        })
      );
  }

  private pickAccessTokenFromAutologinResponse(res: any): string {
    if (!res || typeof res !== "object") {
      return "";
    }
    const nested = res.data ?? res.result;
    if (typeof res.token === "string" && res.token.length > 0) {
      return res.token;
    }
    if (typeof nested?.token === "string" && nested.token.length > 0) {
      return nested.token;
    }
    if (typeof res.accessToken === "string" && res.accessToken.length > 0) {
      return res.accessToken;
    }
    if (typeof res.access_token === "string" && res.access_token.length > 0) {
      return res.access_token;
    }
    return "";
  }
}
